import { Driver, DriverVehiclePair, VehicleSummary } from "../types/models";
import { HttpClient } from "./http-client";

export class DriverService {
  private readonly http: HttpClient;

  constructor(apiUrl: string) {
    this.http = new HttpClient(apiUrl);
  }

  async getAllDrivers(): Promise<Driver[]> {
    return this.http.get<Driver[]>("/api/gestionnaire/chauffeurs");
  }

  /**
   * Tous les chauffeurs, toutes entreprises confondues. Réservé au rôle Admin
   * côté back (contrairement à `getAllDrivers`, ouvert à tout utilisateur connecté).
   */
  async listAllDrivers(): Promise<Driver[]> {
    return this.http.get<Driver[]>("/api/v2/chauffeurs");
  }

  /**
   * Récupère un chauffeur par son id.
   *
   * Le back n'expose pas encore de `GET /chauffeurs/{id}` : on filtre donc la
   * liste complète ici plutôt que dans les pages, pour n'avoir qu'un seul
   * endroit à modifier le jour où l'endpoint existe.
   */
  async getDriverById(driverId: number): Promise<Driver | undefined> {
    const drivers = await this.getAllDrivers();

    return drivers.find((driver) => driver.id === driverId);
  }

  async loadDocumentFile(
    driverId: number,
    entrepriseId: number,
    documentId: number,
  ): Promise<Blob> {
    return this.http.getBlob(
      `/api/entreprises/${entrepriseId}/chauffeur/${driverId}/document/${documentId}`,
    );
  }

  /**
   * Verso d'un document. Le back répond 404 si le document n'en a pas : n'appeler
   * que si `idDocumentFileVerso` est renseigné sur la fiche.
   */
  async loadDocumentVersoFile(
    driverId: number,
    entrepriseId: number,
    documentId: number,
  ): Promise<Blob> {
    return this.http.getBlob(
      `/api/entreprises/${entrepriseId}/chauffeur/${driverId}/document/${documentId}/verso`,
    );
  }

  async addDocumentDriver(
    driverId: number,
    entrepriseId: number,
    documentFormData: FormData,
  ): Promise<void> {
    await this.http.post(
      `/api/entreprises/${entrepriseId}/chauffeur/${driverId}/document`,
      documentFormData,
    );
  }

  async deleteDocumentDriver(
    driverId: number,
    entrepriseId: number,
    documentId: number,
  ): Promise<void> {
    await this.http.delete(
      `/api/entreprises/${entrepriseId}/chauffeur/${driverId}/document/${documentId}`,
    );
  }

  async updateDriver(
    driverId: number,
    entrepriseId: number,
    driver: any,
  ): Promise<void> {
    await this.http.put(
      `/api/entreprises/${entrepriseId}/chauffeur/${driverId}`,
      driver,
    );
  }

  /**
   * Donne le rôle de chauffeur à un contact de l'entreprise. Le back réactive
   * la fiche existante le cas échéant : un chauffeur retiré puis réintégré
   * retrouve ses documents.
   */
  async createDriver(entrepriseId: number, idContact: number): Promise<void> {
    await this.http.post(`/api/entreprises/${entrepriseId}/chauffeur`, {
      idContact,
    });
  }

  /** Véhicules que le chauffeur est autorisé à conduire. */
  async getDriverVehicles(
    driverId: number,
    entrepriseId: number,
  ): Promise<VehicleSummary[]> {
    return this.http.get<VehicleSummary[]>(
      `/api/v2/entreprises/${entrepriseId}/chauffeurs/${driverId}/vehicules`,
    );
  }

  /**
   * Toutes les paires chauffeur/véhicule autorisées, toutes entreprises
   * confondues (réservé au rôle Admin). Chauffeurs et véhicules désactivés
   * sont exclus par le back.
   */
  async getAuthorizedVehiclePairs(): Promise<DriverVehiclePair[]> {
    return this.http.get<DriverVehiclePair[]>(
      "/api/v2/chauffeurs/vehicules-autorises",
    );
  }

  /**
   * Remplace la sélection complète du chauffeur (sémantique PUT) : envoyer la
   * liste voulue, pas un delta. Une liste vide retire toutes les autorisations.
   */
  async setDriverVehicles(
    driverId: number,
    entrepriseId: number,
    idsVehicules: number[],
  ): Promise<void> {
    await this.http.put(
      `/api/v2/entreprises/${entrepriseId}/chauffeurs/${driverId}/vehicules`,
      { idsVehicules },
    );
  }

  /** Désactive la fiche chauffeur ; `createDriver` la restaure. */
  async deleteDriver(driverId: number, entrepriseId: number): Promise<void> {
    await this.http.delete(
      `/api/entreprises/${entrepriseId}/chauffeur/${driverId}`,
    );
  }
}
